import { useQueryClient } from '@tanstack/react-query';
import { useGetProForma } from 'queries/ProForma';
import React, {
    createContext, useContext, useState, useRef,
} from 'react';
import {
    generatePath, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { URLS } from 'services/urls';
import { IProFormaData } from 'views/ProFormaTable/types';

interface StepHandler {
    validate: () => boolean | Promise<boolean>;
    getData: (isDraft?: boolean) => void;
}

interface ProFormaWizardContextType {
    currentStep: number;
    stepHandlers: Record<number, StepHandler>;
    stepData: Record<number, any>;
    registerStepHandler: (step: number, handler: StepHandler) => void;
    handleNextStep: () => void;
    handlePrevStep: () => void;
    saveAsDraft: () => void;
    getStepData: (step: number) => any;
    setStepData: (step: number, data: any) => void;
    isCurrentStepValid: boolean;
    setStepValidationStatus: (isValid: boolean) => void;
    isLoading: boolean;
    proForma: IProFormaData;
}

interface ProFormaWizardProviderProps {
    children: React.ReactNode;
}

const ProFormaWizardContext = createContext<ProFormaWizardContextType | null>(null);

export const ProFormaWizardProvider: React.FC<ProFormaWizardProviderProps> = ({ children }) => {
    const [params] = useSearchParams();
    const queryClient = useQueryClient();
    const draftStep = Number(params.get('step'));
    const [currentStep, setCurrentStep] = useState<number>(draftStep !== null && Number(draftStep) > 0 ? Number(draftStep) : 1);
    const [stepData, setStepDataState] = useState<Record<number, any>>({});
    const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const stepHandlers = useRef<Record<number, StepHandler>>({});
    const navigate = useNavigate();
    const { dealId, proFormaId } = useParams<{ dealId: string; proFormaId: string }>();

    const totalSteps = 3;

    const { data: proForma } = useGetProForma(Number(dealId), Number(proFormaId), {
        enabled: Boolean(dealId) && Boolean(proFormaId),
    });

    const setStepData = (step: number, data: any) => {
        setStepDataState(prev => ({ ...prev, [step]: data }));
    };

    const getStepData = (step: number) => stepData[step] || null;

    const registerStepHandler = (step: number, handler: StepHandler) => {
        stepHandlers.current[step] = handler;
    };

    const navigateToProFormaHome = () => {
        queryClient.invalidateQueries({ queryKey: ['getProFormas'] });
        const path = generatePath(URLS.PRO_FORMA.HOME, { dealId });
        navigate(path);
    };

    const saveAsDraft = async () => {
        const handler = stepHandlers.current[currentStep];
        try {
            setIsLoading(true);
            if (handler?.getData) {
                await handler.getData(true);
            }
            navigateToProFormaHome();
        } catch (error) {
            console.error(`Error saving draft for step ${currentStep}:`, error);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const removeStepSearchParam = () => {
        const path = generatePath(URLS.PRO_FORMA.WIZARD, { dealId, proFormaId });
        navigate(path);
    };

    const handleNextStep = async () => {
        const handler = stepHandlers.current[currentStep];

        // Validate current step
        if (handler?.validate && !(await handler.validate())) {
            console.error(`Validation failed for step ${currentStep}`);
            return;
        }

        try {
            setIsLoading(true);
            if (handler?.getData) {
                await handler.getData();
            }

            if (currentStep === totalSteps) {
                queryClient.invalidateQueries({ queryKey: ['getProFormas'] });
                const path = generatePath(URLS.PRO_FORMA.TABLE, {
                    dealId: dealId || '',
                    proFormaId: proFormaId || '',
                });
                navigate(path);
            } else {
                setCurrentStep(prev => prev + 1);
                removeStepSearchParam();
            }
        } catch (error) {
            console.error(`Error processing step ${currentStep}:`, error);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrevStep = () => {
        if (currentStep === 1) {
            navigateToProFormaHome();
        } else {
            setCurrentStep(prev => prev - 1);
            removeStepSearchParam();
        }
    };

    const setStepValidationStatus = (isValid: boolean) => {
        setIsCurrentStepValid(isValid);
    };

    return (
        <ProFormaWizardContext.Provider
            value={{
                currentStep,
                stepHandlers: stepHandlers.current,
                stepData,
                registerStepHandler,
                handleNextStep,
                handlePrevStep,
                saveAsDraft,
                setStepData,
                getStepData,
                isCurrentStepValid,
                setStepValidationStatus,
                isLoading,
                proForma,
            }}
        >
            {children}
        </ProFormaWizardContext.Provider>
    );
};

export const useProFormaWizard = (): ProFormaWizardContextType => {
    const context = useContext(ProFormaWizardContext);
    if (!context) {
        throw new Error('useProFormaWizard must be used within a ProFormaWizardProvider');
    }
    return context;
};
